import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sürükle-bırak sıralı liste (GenericFormDrawer `sortableList` alanı). Değer = sıralı id dizisi;
// `items` etiketleri taşır. Sürükleme deseni SurveyCreatePage'deki soru sıralamasıyla aynı.

const Label = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  font-size: 14px;
`;

const Badge = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  min-width: 28px;
`;

function SortableRow({ id, label, badge }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <Row ref={setNodeRef} style={style} data-testid={`sortable-row-${id}`}>
      <GripVertical size={16} style={{ color: '#A0A8B4', cursor: 'grab' }} {...attributes} {...listeners} />
      {badge != null && <Badge>{badge}</Badge>}
      <span style={{ flex: 1 }}>{label}</span>
    </Row>
  );
}

/**
 * @param {Array<{id: string, label: string}>} items
 * @param {string[]} value   sıralı id dizisi (boşsa items sırası)
 * @param {(ids: string[]) => void} onChange
 * @param {(index: number, count: number) => (string|number|null)} [badge]  satır başı rozeti (ör. seviye)
 */
export default function SortableList({ label, items = [], value, onChange, badge }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const order = useMemo(() => {
    const known = new Set(items.map((i) => i.id));
    const base = (value && value.length ? value : items.map((i) => i.id)).filter((id) => known.has(id));
    // Değerde olmayan yeni öğeler sona eklenir — hiçbir öğe kaybolmaz.
    items.forEach((i) => { if (!base.includes(i.id)) base.push(i.id); });
    return base;
  }, [items, value]);

  const labelById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i.label])), [items]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onChange(arrayMove(order, order.indexOf(active.id), order.indexOf(over.id)));
  }, [order, onChange]);

  return (
    <div>
      {label && <Label>{label}</Label>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          {order.map((id, index) => (
            <SortableRow key={id} id={id} label={labelById[id]} badge={badge ? badge(index, order.length) : null} />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

// Test ve sayfa kullanımı için saf yardımcı: sürükleme sonucu (eski → yeni konum).
export const moveId = (order, fromId, toId) => arrayMove(order, order.indexOf(fromId), order.indexOf(toId));
